<template>
  <div
    class="book-meeting-modal flex flex-col h-full bg-white rounded-2xl overflow-hidden shadow-2xl"
  >
    <!-- Deep Dark Blue Header Section -->
    <header
      class="px-4 sm:px-6 py-4 sm:py-4.5 bg-slate-900 text-white flex items-center justify-between border-b border-slate-850 relative"
    >
      <!-- Background Pulse Effect -->
      <div
        class="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-teal-400 via-teal-600 to-transparent pointer-events-none"
      ></div>

      <div class="relative z-10 flex items-center gap-2">
        <i class="fal fa-calendar-alt text-teal-400 text-lg"></i>
        <h2 class="text-base font-extrabold tracking-tight">
          {{ $t('Components.BookMeetingModal.title', tLocale) }}
        </h2>
      </div>
      <button
        type="button"
        class="close-btn relative z-10 text-slate-400 hover:text-white transition-colors text-xl leading-none rounded-full hover:bg-white/10 flex items-center justify-center focus:outline-none"
        @click="close"
        :aria-label="$t('Components.BookMeetingModal.close', tLocale)"
      >
        &times;
      </button>
    </header>

    <div
      class="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6"
    >
      <!-- Introduction Box -->
      <div
        class="p-4 bg-teal-50/40 border border-teal-100/50 rounded-2xl flex gap-3"
      >
        <i class="fas fa-handshake text-teal-600 text-sm mt-0.5 shrink-0"></i>
        <div class="text-xs text-slate-600 font-medium leading-relaxed">
          {{
            $t('Components.BookMeetingModal.intro', tLocale, { sponsorName })
          }}
        </div>
      </div>

      <!-- Success Handling -->
      <div v-if="success" class="text-center py-8 space-y-4 animate-scale-in">
        <div
          class="w-16 h-16 rounded-full bg-emerald-50 text-emerald-500 flex items-center justify-center mx-auto border border-emerald-100 shadow-sm"
        >
          <i class="fas fa-check text-2xl"></i>
        </div>
        <div class="space-y-1">
          <h3 class="text-base font-extrabold text-slate-900">
            {{ $t('Components.BookMeetingModal.successHeadline', tLocale) }}
          </h3>
          <p class="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
            {{ $t('Components.BookMeetingModal.successBody', tLocale) }}
          </p>
        </div>
      </div>

      <!-- Form Content -->
      <template v-else>
        <!-- Slot Picker Section -->
        <div class="space-y-3">
          <div class="flex items-center justify-between">
            <span
              class="text-xxs font-extrabold uppercase tracking-widest text-slate-400"
            >
              {{ $t('Components.BookMeetingModal.selectSlot', tLocale) }}
            </span>
            <span
              class="text-xxs text-teal-600 font-semibold"
              v-if="selectedSlotId"
            >
              {{ $t('Components.BookMeetingModal.slotSelected', tLocale) }}
            </span>
          </div>

          <div
            class="bg-slate-50 border border-slate-200/80 rounded-2xl p-4.5 shadow-inner"
          >
            <AvailableSlotsDataProvider
              v-if="sponsorId"
              :sponsor-id="sponsorId"
              v-slot="{ slots, loading }"
            >
              <SlotPicker
                :slots="slots"
                :loading="loading"
                :selected-id="selectedSlotId"
                :english="english"
                @select="selectSlot"
              >
                <template #empty>
                  <button
                    type="button"
                    class="mt-3 text-xs font-bold text-teal-600 hover:text-teal-700 hover:underline inline-flex items-center gap-1"
                    @click="goFreeform"
                  >
                    {{
                      $t(
                        'Components.BookMeetingModal.requestWithoutSlot',
                        tLocale
                      )
                    }}
                    <i class="fal fa-arrow-right text-xxs"></i>
                  </button>
                </template>
              </SlotPicker>
            </AvailableSlotsDataProvider>
          </div>
        </div>

        <!-- Custom / Freeform Timing -->
        <transition
          enter-active-class="transition ease-out duration-200"
          enter-from-class="opacity-0 -translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
        >
          <div
            v-if="freeformEnabled"
            class="space-y-2.5 p-4 bg-slate-50 border border-slate-200 rounded-2xl"
          >
            <div
              class="text-xxs font-extrabold uppercase tracking-widest text-slate-400"
            >
              {{ $t('Components.BookMeetingModal.preferredTime', tLocale) }}
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div class="space-y-1">
                <label
                  class="text-[10px] text-slate-400 font-bold uppercase tracking-wider"
                  >{{
                    $t('Components.BookMeetingModal.startTime', tLocale)
                  }}</label
                >
                <input
                  type="datetime-local"
                  v-model="freeformStart"
                  class="w-full px-3 py-2 bg-white rounded-xl border border-slate-250 text-xs text-slate-800 font-medium focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10 outline-none transition-all"
                />
              </div>
              <div class="space-y-1">
                <label
                  class="text-[10px] text-slate-400 font-bold uppercase tracking-wider"
                  >{{
                    $t('Components.BookMeetingModal.endTime', tLocale)
                  }}</label
                >
                <input
                  type="datetime-local"
                  v-model="freeformEnd"
                  class="w-full px-3 py-2 bg-white rounded-xl border border-slate-250 text-xs text-slate-800 font-medium focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10 outline-none transition-all"
                />
              </div>
            </div>
          </div>
        </transition>

        <!-- Meeting Location -->
        <div class="space-y-2">
          <label
            class="block text-xxs font-extrabold uppercase tracking-widest text-slate-400"
          >
            {{ $t('Components.BookMeetingModal.locationLabel', tLocale) }}
          </label>
          <input
            v-model="location"
            :placeholder="
              $t('Components.BookMeetingModal.locationPlaceholder', tLocale)
            "
            type="text"
            class="w-full px-4 py-3 bg-slate-50 text-slate-800 text-xs rounded-2xl border border-slate-200 focus:border-teal-500 focus:bg-white focus:ring-4 focus:ring-teal-500/5 outline-none transition-all leading-relaxed placeholder-slate-400 font-medium"
          />
        </div>

        <!-- Message/Discussion Topics Textarea -->
        <div class="space-y-2">
          <div class="flex items-center justify-between">
            <label
              class="block text-xxs font-extrabold uppercase tracking-widest text-slate-400"
            >
              {{ $t('Components.BookMeetingModal.messageLabel', tLocale) }}
            </label>
            <span
              class="text-xxs text-slate-400 font-bold uppercase tracking-wider"
            >
              {{ message.length }}/500
            </span>
          </div>
          <textarea
            v-model="message"
            :placeholder="
              $t('Components.BookMeetingModal.messagePlaceholder', tLocale)
            "
            rows="3.5"
            maxlength="500"
            class="w-full px-4 py-3 bg-slate-50 text-slate-800 text-xs rounded-2xl border border-slate-200 focus:border-teal-500 focus:bg-white focus:ring-4 focus:ring-teal-500/5 outline-none transition-all leading-relaxed placeholder-slate-400 font-medium"
          ></textarea>
        </div>

        <div
          v-if="error"
          ref="errorBox"
          class="text-xs font-bold text-red-500 bg-red-50/50 border border-red-100 p-3 rounded-xl flex gap-2"
        >
          <i class="fal fa-exclamation-circle text-sm shrink-0"></i>
          <span>{{ error }}</span>
        </div>
      </template>
    </div>

    <!-- Modal Footer Actions -->
    <footer
      class="px-4 sm:px-6 py-3 sm:py-4.5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2 sm:gap-3 shrink-0"
    >
      <button
        v-if="!success"
        type="button"
        class="flex-1 sm:flex-initial px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider text-slate-500 hover:text-slate-850 hover:bg-slate-200/50 rounded-xl transition-all min-h-[44px]"
        @click="close"
      >
        {{ $t('Components.BookMeetingModal.cancel', tLocale) }}
      </button>

      <button
        v-if="!success"
        type="button"
        :disabled="!canSubmit || submitting"
        class="flex-1 sm:flex-initial px-5 py-2.5 rounded-xl btn-brand text-xs font-extrabold uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-all duration-200 flex items-center justify-center gap-2 min-h-[44px]"
        @click="submit"
      >
        <i v-if="submitting" class="fal fa-spinner-third animate-spin"></i>
        <span>{{
          submitting
            ? $t('Components.BookMeetingModal.submitting', tLocale)
            : $t('Components.BookMeetingModal.submit', tLocale)
        }}</span>
      </button>

      <button
        v-if="success"
        type="button"
        class="w-full sm:w-auto px-5 py-2.5 rounded-xl btn-brand text-xs font-extrabold uppercase tracking-widest transition-all shadow-sm min-h-[44px] flex items-center justify-center"
        @click="goToMyMeetings"
      >
        {{ $t('Components.BookMeetingModal.viewMyMeetings', tLocale) }}
      </button>
    </footer>
  </div>
</template>

<script>
import SlotPicker from '../shared/SlotPicker.vue';
import { composeEventIso } from '@/helpers/event-time';

export default {
  name: 'BookMeetingModal',
  components: { SlotPicker },
  props: {
    sponsorId: { type: String, required: true },
    sponsorName: { type: String, default: '' },
    boothLabel: { type: String, default: '' },
    // The AI Matcher section is English-only, but this modal is also opened from the lobby
    // sponsor page, which follows the event language. Callers inside the AI Matcher pass true.
    english: { type: Boolean, default: false }
  },
  data() {
    return {
      selectedSlotId: null,
      freeformEnabled: false,
      freeformStart: '',
      freeformEnd: '',
      location: '',
      message: '',
      submitting: false,
      error: null,
      success: false,
      wrapperEl: null
    };
  },
  mounted() {
    this.location = this.boothLabel
      ? this.$t('Components.BookMeetingModal.boothLocation', this.tLocale, {
          booth: this.boothLabel
        })
      : '';
    // vue-js-modal wraps slotted content in `.vm--modal` and constrains it via inline
    // width/height styles from the modal options. To get a full-screen sheet on mobile
    // without changing every call site, walk up to the wrapper and tag it; CSS below
    // overrides the inline sizing at <sm. This avoids :has() (not supported on iOS 11).
    this.$nextTick(() => {
      const wrapper =
        this.$el && this.$el.closest && this.$el.closest('.vm--modal');
      if (wrapper) {
        wrapper.classList.add('book-meeting-modal__wrapper');
        this.wrapperEl = wrapper;
      }
    });
  },
  beforeDestroy() {
    if (this.wrapperEl)
      this.wrapperEl.classList.remove('book-meeting-modal__wrapper');
  },
  computed: {
    tLocale() {
      return this.english ? 'en' : this.$i18n.locale;
    },
    canSubmit() {
      if (this.success) return false;
      if (this.selectedSlotId) return true;
      if (this.freeformEnabled && this.freeformStart && this.freeformEnd)
        return true;
      return false;
    },
    eventTz() {
      return this.$store.getters['CurrentEvent/getTimezone'];
    },
    eventMode() {
      return this.$store.getters['CurrentEvent/getTimezoneMode'];
    }
  },
  methods: {
    selectSlot(slot) {
      this.selectedSlotId = slot.id;
      this.freeformEnabled = false;
    },
    goFreeform() {
      this.freeformEnabled = true;
      this.selectedSlotId = null;
    },
    close() {
      document.body.classList.remove('overflow-hidden');
      this.$emit('close');
    },
    async submit() {
      this.submitting = true;
      this.error = null;
      try {
        const payload = {
          sponsorId: this.sponsorId,
          message: this.message || null
        };
        payload.location = this.location ? this.location.trim() : null;
        if (this.selectedSlotId) {
          payload.timeSlotId = this.selectedSlotId;
        } else if (this.freeformEnabled) {
          // Interpret the entered wall-clock time in the event zone (Static) or local (Dynamic).
          payload.startAt = composeEventIso(
            this.freeformStart,
            this.eventTz,
            this.eventMode
          );
          payload.endAt = composeEventIso(
            this.freeformEnd,
            this.eventTz,
            this.eventMode
          );
        }
        await this.$store.dispatch('MyMeetings/book', payload);
        this.success = true;
      } catch (err) {
        this.error =
          err?.response?.data?.message ||
          this.$t('Components.BookMeetingModal.errorGeneric', this.tLocale);
        this.$nextTick(() => {
          if (this.$refs.errorBox && this.$refs.errorBox.scrollIntoView) {
            this.$refs.errorBox.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest'
            });
          }
        });
      } finally {
        this.submitting = false;
      }
    },
    goToMyMeetings() {
      this.close();
      const slug = this.$store.getters['CurrentEvent/getEventSlug'];
      const target = slug ? `/meetings` : '/meetings';
      this.$router.push(target).catch(() => {});
    }
  }
};
</script>

<style scoped>
.px-4\.5 {
  padding-left: 1.125rem;
  padding-right: 1.125rem;
}
.py-4\.5 {
  padding-top: 1.125rem;
  padding-bottom: 1.125rem;
}
.bg-slate-850 {
  background-color: #0f172a;
}
.border-slate-250 {
  border-color: #cbd5e1;
}
.text-xxs {
  font-size: 0.65rem;
}
.animate-scale-in {
  animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Strictly prevent FontAwesome icons/SVGs from expanding enormously */
i.fal,
i.fas,
i.far,
i.fab,
svg {
  width: 1.125rem !important;
  height: 1.125rem !important;
  min-width: 1.125rem !important;
  min-height: 1.125rem !important;
  max-width: 1.125rem !important;
  max-height: 1.125rem !important;
  font-size: 1.125rem !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
}

/* Spinner Icon */
.fa-spinner-third {
  width: 1.25rem !important;
  height: 1.25rem !important;
  min-width: 1.25rem !important;
  min-height: 1.25rem !important;
  font-size: 1.25rem !important;
}

/* Close button: 44x44 mobile tap target, 32x32 chip on desktop. */
.close-btn {
  width: 2.75rem !important;
  height: 2.75rem !important;
  min-width: 2.75rem !important;
  min-height: 2.75rem !important;
}
@media (min-width: 640px) {
  .close-btn {
    width: 2rem !important;
    height: 2rem !important;
    min-width: 2rem !important;
    min-height: 2rem !important;
  }
}

/* Success Check icon */
.w-16 i,
.w-16 svg {
  width: 1.75rem !important;
  height: 1.75rem !important;
  min-width: 1.75rem !important;
  min-height: 1.75rem !important;
  max-width: 1.75rem !important;
  max-height: 1.75rem !important;
  font-size: 1.75rem !important;
}

/* Form input fields & textareas premium focus states */
input:focus,
textarea:focus {
  border-color: var(--color-primary) !important;
  box-shadow: 0 0 0 3px rgb(var(--brand-ramp-600) / 0.15) !important;
  background-color: #ffffff !important;
}
</style>

<!-- Unscoped: targets the vue-js-modal wrapper sitting outside this component's
     scoped tree. Below 640px we expand it to a full-screen sheet; at >=640px the
     inline styles from $modal.show() are untouched so the centered card stays
     pixel-identical on desktop. -->
<style>
@media (max-width: 639px) {
  .vm--modal.book-meeting-modal__wrapper {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    height: 100% !important;
    max-height: 100% !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    border-radius: 0 !important;
    transform: none !important;
    margin: 0 !important;
  }
  .vm--modal.book-meeting-modal__wrapper .book-meeting-modal {
    border-radius: 0;
    height: 100%;
  }
}
</style>
